<template>
  <div class="relative w-full h-full flex justify-center bg-black">
    <div class="interactive-video  h-full bg-black" :class="{ 'pop-content-show': popContentShow }">
      <transition name="fade">
        <Video
          v-if="lastVideoContent"
          ref="videoEle"
          :content="lastVideoContent"
          :isCurrentContent="isCurVideo"
          :isInLookBack="isInLookBack || portalView"
          @next="handleNext"
          @changeTitleVisible="changeTitleVisible"
        />
      </transition>
      <transition name="fade">
        <component
          :is="getNotVideoDispatcher(lastContent)"
          :key="lastContent.$id"
          :content="lastContent"
          :isInLookBack="isInLookBack || portalView"
          @next="handleNext"
          @changeTitleVisible="changeTitleVisible"
          @toggleTitleShow="toggleTitleShow"
        />
      </transition>

      <transition name="pop">
        <div v-if="popContentShow" class="absolute w-full h-full bottom-0 left-0 z-10">
          <div class="mask absolute w-full h-full" @click="toggleTitleShow" />
          <transition name="comp-pop">
            <component
              :is="popDispatcher(lastContent)"
              :key="lastContent.$id + (sequence.contents.length - 1)"
              class="pop-component"
              :content="lastContent"
              :characters="sequence.characters"
              :isInLookBack="isInLookBack || portalView"
              @next="handleNext"
            />
          </transition>
        </div>
      </transition>

      <transition name="fade">
        <div
          v-show="isTitleShow || isTitleHover"
          class="interactive-video-title flex items-center justify-center absolute left-0 top-0"
          :class="{ 'title-with-text': portalView }"
          @mouseover="handleTitleHover"
          @mouseleave="handleMouseLeaveTitle"
        >
          <div
            v-if="isReturnBtnShow"
            class="return-icon"
            :class="[portalView ? 'return-icon-text' : 'return-icon-circle']"
            @click="handleExit"
          >
            <img src="./assets/return-icon.svg" alt="" />
            <span v-if="portalView">返回课堂</span>
          </div>

          <span>{{ sequence.title }}</span>
          <img v-if="isEnd" class="finish-icon" src="./assets/finish-icon.svg" alt="" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { Sequence, Content } from '@forchange/storytime';
import { Vue, Prop, Component } from 'vue-property-decorator';
import Dispatcher from '@/pages/main/models/Dispatcher';
import { VideoChoice, VideoShortAnswer, VideoGraphic } from '@/components/resources';
import VideoImg from '@/components/resources/interactive-video/VideoImg';
import Video from '@/components/resources/interactive-video/Video';
import VideoEnd from '@/components/resources/interactive-video/VideoEnd';
import VideoIframe from '@/components/resources/interactive-video/VideoIframe';

import smoothscroll from 'smoothscroll-polyfill';
smoothscroll.polyfill();

const popDispatcher = new Dispatcher({
  VideoRadio: VideoChoice,
  VideoCheckbox: VideoChoice,
  VideoShortAnswer,
  VideoGraphic
});

const NotVideoDispatcher = new Dispatcher({
  VideoImage: VideoImg,
  VideoEnd,
  VideoIframe
});

@Component({
  components: {
    VideoChoice,
    VideoShortAnswer,
    VideoGraphic,
    VideoImg,
    Video,
    VideoEnd,
    VideoIframe
  }
})
export default class InteractiveVideo extends Vue {
  @Prop({ type: Object }) sequence!: Sequence & {
    title: string;
  };
  @Prop() isInLookBack!: boolean;

  isTitleShow = false;
  //
  isTitleHover = false;

  // 作为卡片回看时使用
  portalIndex = 0;

  get portalView() {
    const portalSequence = this.$store.state.common.portalSequence;
    return portalSequence[portalSequence.length - 1];
  }

  get sequenceContent(): Content[] {
    if (this.portalView) return this.portalView.contents;
    return this.sequence.contents;
  }

  get lastVideoContent() {
    let contentArr = this.sequenceContent;
    if (this.portalView) {
      contentArr = contentArr.slice(0, this.portalIndex + 1);
    }

    const contents = contentArr.filter((item) => item.$kind === 'VideoInfo');
    return contents[contents.length - 1];
  }

  get isCurVideo() {
    return this.lastContent.$kind === 'VideoInfo';
  }
  get isEnd() {
    return this.lastContent.$kind === 'VideoEnd';
  }

  get lastContent(): Content {
    if (this.portalView) return this.sequenceContent[this.portalIndex];
    return this.sequenceContent[this.sequenceContent.length - 1];
  }

  get popContentShow() {
    return this.popDispatcher(this.lastContent);
  }
  get isNotVideoContentShow() {
    return this.getNotVideoDispatcher(this.lastContent);
  }

  get isReturnBtnShow() {
    return !this.$store.state.common.isPublic;
  }

  popDispatcher(content: Content) {
    return popDispatcher.dispatch(content);
  }

  getNotVideoDispatcher(content: Content) {
    return NotVideoDispatcher.dispatch(content);
  }

  changeTitleVisible(visible?: boolean) {
    this.isTitleShow = !!visible;
  }

  trackVideoUnload() {
    if (this.isCurVideo) {
      const video = this.$refs.videoEle as Video;
      video?.onBeforeUnload?.();
    }
  }

  handleTitleHover() {
    this.isTitleHover = true;
  }
  handleMouseLeaveTitle() {
    this.isTitleHover = false;
  }

  handleNext() {
    this.changeTitleVisible();
    // 作为卡片时的逻辑
    if (this.portalView) {
      if (this.portalIndex + 1 === this.sequenceContent.length) {
        const portal = this.portalView as Sequence & {
          scrollTop: number;
        };

        this.portalIndex = 0;

        this.$store.commit('common/setCallStatus', {
          isroll: false,
          rollHeight: portal.scrollTop
        });
        this.$store.commit('common/setLoading', true);
        this.$store.commit('common/popPortal');
      } else {
        this.portalIndex++;
      }
    } else {
      window.next();
    }
  }

  handleExit() {
    if (this.portalView) {
      this.$store.commit('common/setCallStatus', {
        isroll: false,
        rollHeight: this.portalView.scrollTop
      });
      this.$store.commit('common/setLoading', true);
      this.$store.commit('common/popPortal');
    } else {
      this.$PostConnection?.command('goback_to_center');
    }
  }

  toggleTitleShow() {
    this.changeTitleVisible(!this.isTitleShow);
  }

  mounted() {
    this.$PostConnection?.command('nav_hide', {
      animation: false
    });

    console.log(this.getNotVideoDispatcher(this.lastContent));
  }
}
</script>

<style lang="scss" scoped>
$d-time: 0.4s;
.interactive-video {
  position: relative;
  width: 100%;
  max-width: 768px;

  .interactive-video-title {
    z-index: 12;
    width: 100%;
    height: 56px;
    padding: 0 54px;
    color: #fff;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    background: linear-gradient(360deg, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, 0.6) 100%);
    .return-icon {
      position: absolute;
      left: 20px;
      display: flex;
      height: 24px;
      color: #ffffff;
      font-size: 12px;
      background: rgba(11, 12, 19, 0.3);
      cursor: pointer;
      img {
        width: 6.74px;
      }

      &:hover {
        opacity: 0.85;
      }
    }
    .return-icon-circle {
      width: 24px;
      padding: 6px 7px;
      border-radius: 50%;
    }
    .return-icon-text {
      padding: 3px 7px;
      font-size: 12px;
      border-radius: 26px;
      span {
        line-height: 17px;
      }

      img {
        margin-right: 8px;
      }
    }

    .finish-icon {
      position: absolute;
      right: 20px;
      width: 24px;
    }
    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .title-with-text {
    padding-left: 100px;
  }

  .mask {
    background: rgba(11, 12, 19, 0.3);
    backdrop-filter: blur(10px);
  }

  .pop-enter,
  .pop-leave-to {
    .mask {
      opacity: 0;
    }
    .pop-component {
      transform: translateY(100%);
    }
  }
  .pop-enter-active,
  .pop-leave-active {
    transition: all $d-time;
    .mask,
    .pop-component {
      transition: all $d-time ease-in-out;
    }
  }

  .comp-pop-enter,
  .comp-pop-leave-to {
    transform: translateY(100%);
  }
  .comp-pop-enter-active,
  .comp-pop-leave-active {
    transition: all $d-time ease-in-out;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.4s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
<style lang="scss">
.interactive-video.pop-content-show {
  .xgplayer-skin-custom {
    .xgplayer-start {
      display: none;
    }
  }
}
</style>
